import type {
  APIComponentAttributes,
  ArdoqId,
  ComparatorMatcher,
} from '@ardoq/api-types';
import {
  toDrawableShapeName,
  type EnhancedScopeData,
  type RepresentationData,
} from '@ardoq/data-model';
import {
  colorsAndRepresentations,
  enhancedScopeDataOperations,
} from '@ardoq/scope-data';
import { getDefaultCSSColor } from '@ardoq/color-helpers';
import { isComponentIncludedInContextByFilter } from '@ardoq/filter-interface';

export type ModelIdStyle = {
  color: string;
  representationData: RepresentationData;
};

export type ComponentStyle = 'icon' | 'shape';

/**
 * In shape mode a component renders as its shape instead of its icon, unless it
 * has an image (which always wins). The shape falls back from the component's own
 * shape to its type's, mirroring the modernized Block Diagram.
 *
 * The shape *replaces* the representation rather than joining it: the aq-blocks
 * node renderer draws an icon in preference to a shape, so a representation
 * carrying both would keep drawing the icon.
 */
const withComponentStyle = ({
  representationData,
  componentStyle,
  shapeName,
}: {
  representationData: RepresentationData;
  componentStyle: ComponentStyle;
  shapeName: string | null;
}): RepresentationData =>
  componentStyle === 'shape' && !representationData.isImage && shapeName
    ? {
        ...representationData,
        shapeName: toDrawableShapeName(shapeName),
        icon: undefined,
        value: null,
      }
    : representationData;

/**
 * The shape a component renders as in shape mode: its own, falling back to its
 * type's only when the component carries neither its own image nor its own icon —
 * the precedence `modernizedBlockDiagramRepresentationData` applies. Truthiness
 * throughout, so a cleared shape (`''`) falls through to the type rather than
 * suppressing it.
 */
const resolveShapeName = (
  scopeData: EnhancedScopeData,
  component: APIComponentAttributes
): string | null => {
  if (component.shape) {
    return component.shape;
  }
  if (component.image || component.icon) {
    return null;
  }
  return (
    enhancedScopeDataOperations.getComponentTypeByModelIdAndTypeId(
      scopeData,
      component.model,
      component.typeId
    )?.shape ?? null
  );
};

/**
 * The fill color and representation (icon/image) for every component and component type
 * in scope, keyed by model id. A component resolves to its display color — the
 * conditional-formatting color when a formatting filter matches, else its component/type
 * color — and to its own representation, honouring a per-component icon/image override
 * before falling back to the type's. A component type resolves to its type color and
 * type representation. The graph builders look this up by a node's or group's `modelId`,
 * so leaves and component-backed groups (parent-all, reference) pick up the component
 * style while group-by-type groups pick up the type style — no group-type branching.
 *
 * Called from the view-model stream (which supplies the active filters from
 * `formattingAndLabelRules$`), keeping filter evaluation at the stream boundary.
 */
export const buildStyleByModelId = (
  scopeData: EnhancedScopeData,
  filters: ComparatorMatcher[],
  componentStyle: ComponentStyle = 'icon'
): Map<ArdoqId, ModelIdStyle> => {
  const styles = new Map<ArdoqId, ModelIdStyle>();
  for (const [modelId, modelTypes] of Object.entries(
    scopeData.typesByModelId
  )) {
    for (const [typeId, type] of Object.entries(
      modelTypes.componentTypesById
    )) {
      styles.set(typeId, {
        color: type.color || getDefaultCSSColor(type.level),
        representationData: withComponentStyle({
          representationData:
            colorsAndRepresentations.getComponentTypeRepresentationData(
              scopeData,
              typeId,
              modelId
            ),
          componentStyle,
          shapeName: type.shape,
        }),
      });
    }
  }
  for (const [componentId, component] of Object.entries(
    scopeData.componentsById
  )) {
    const { color } = isComponentIncludedInContextByFilter(
      component,
      filters,
      // No context component: viewpoint mode has no client-side context filtering.
      undefined,
      scopeData
    );
    styles.set(componentId, {
      color:
        color ??
        enhancedScopeDataOperations.getComponentColor(scopeData, componentId),
      representationData: withComponentStyle({
        representationData:
          colorsAndRepresentations.getComponentRepresentationData(
            scopeData,
            componentId
          ),
        componentStyle,
        shapeName:
          componentStyle === 'shape'
            ? resolveShapeName(scopeData, component)
            : null,
      }),
    });
  }
  return styles;
};
